import { Chip, Grid, Paper, Stack, Typography } from '@mui/material'
import { useEffect, useState } from "react";
import RangeSelectorDatetime from '../common/RangeSelectorDatetime'
import Chart from 'react-apexcharts'
import { useGetFilteredTransactionsMutation } from "../../services/analytics.service";
import moment, { Moment } from 'moment'

const chartStyle = {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  bgcolor: "#ffffff",
  p: 2,
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
};

function TransactionsChart() {
  const [transactions, setTransactions] = useState<any>({});
  const [dataSource, setDataSource] = useState<string[]>(['createdAt']);
  const [pipeline, setPipeline] = useState({
    ranges: [{
      field: dataSource[0],
      start: moment().subtract(1, 'weeks'),
      end: moment()
    }],
    dynamicFilters: [],
    groupOptions: { aggregateField: "amount" }
  });
  const [chartData, setChartData] = useState<any>({ categories: [], series: [] });
  const [getTranscations] = useGetFilteredTransactionsMutation();

  const getFilteredTranscations = async (pipeline: any) => {
    return await getTranscations(pipeline).then((data: any) => {
      const items: any[] = data?.data[0]?.item;
      const amount = data?.data[0].amount;
      const count = data?.data[0].count;
      const groupedValues = Object.groupBy(items, ({ createdAt }: any) => createdAt.split('T')[0]);
      const keys = Object.keys(groupedValues);
      const categories = keys.sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
      return { categories, groupedValues, amount, count };
    }).catch((err) => console.log(err));
  };

  const handleRangeChage = (range: { start: Moment | null; end: Moment | null }) => {
    setPipeline({
      ...pipeline,
      ranges: [{ field: dataSource[0], start: range.start?.toISOString() as any, end: range.end?.toISOString() as any }]
    });
  };

  useEffect(() => {
    const fetch = async () => {
      const result = await getFilteredTranscations(pipeline) as any;
      setTransactions(result);
    };
    fetch();
  }, [dataSource, pipeline]);

  useEffect(() => {
    if (transactions?.categories) {
      const categories = [...transactions.categories].sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
      const unique = Array.from(new Set(categories));
      setChartData({
        categories: unique,
        series: [
          {
            name: 'Count',
            data: unique.map((item: any) => transactions.groupedValues[item]?.length ?? 0)
          },
          {
            name: 'Amount',
            data: unique.map((item: any) =>
              transactions.groupedValues[item]?.reduce((sum: number, a: any) => sum + (a.amount ?? 0), 0) ?? 0
            )
          }
        ]
      });
    }
  }, [dataSource, pipeline, transactions]);

  const handleAddSource = (source: 'createdAt' | 'transactionDate') => {
    setDataSource([source]);
  };

  return (
    <Grid item container flex={3} sx={chartStyle}>
      <Grid item container gap={1} sx={{ flexShrink: 0, mb: 1 }} alignItems="center">
        <RangeSelectorDatetime onRangeChange={handleRangeChage} />
        <Stack sx={{ marginRight: "auto" }} direction="row" spacing={0.5}>
          {(["createdAt", "transactionDate"] as const).map((source) => (
            <Chip
              sx={{ borderRadius: "4px", textTransform: "uppercase", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.5px" }}
              key={source}
              label={source}
              component="button"
              onClick={() => handleAddSource(source)}
              color={dataSource.includes(source) ? "primary" : "default"}
            />
          ))}
        </Stack>
        <Paper elevation={0} sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: "6px" }}>
          <Typography variant="body2" fontWeight={500}>Total Count : {transactions?.count}</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 1, border: "1px solid #E2E8F0", borderRadius: "6px" }}>
          <Typography variant="body2" fontWeight={500}>Total Amount : {transactions?.amount}</Typography>
        </Paper>
      </Grid>

      <Grid item sx={{ flex: 1, minHeight: 0 }}>
        <Chart
          options={{
            chart: { toolbar: { show: false }, background: "transparent" },
            colors: ["#0F172A", "#059669"],
            xaxis: {
              categories: chartData.categories,
              tickAmount: 8,
              labels: {
                rotate: -45,
                hideOverlappingLabels: true,
                style: { fontSize: "10px", colors: "#64748B" }
              }
            },
            yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: "#64748B", fontSize: "11px" } } },
            plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#E2E8F0" },
            tooltip: { theme: "light" },
            legend: { position: "top" },
          }}
          series={chartData.series}
          type="bar"
          height={300}
        />
      </Grid>
    </Grid>
  );
}

export default TransactionsChart;
